import { useEffect } from 'react';

import QRCode from 'qrcode.react';
import {
  ButtonMantine,
  Card,
  FullWidthButtonGroup,
  Modal,
  ModalProps,
  Stack,
  Text,
  notifications,
} from 'ui';
import { useClipboard } from 'ui/Hooks';

import { Printty } from '../../../../../packages/Printty/printty';
import { usePlatformService } from '../../../../hooks/usePlatformService';
import { usePlatformShare } from '../../../../hooks/usePlatformShare';

interface ShareQRCodeModalProps extends ModalProps {
  link: string;
  description?: string;
}

const ShareQRCodeModalContent = ({
  link,
  description,
  printRef,
}: {
  link: string;
  description?: string;
  printRef: React.RefObject<HTMLDivElement>;
}) => {
  return (
    <Stack gap="lg" align="center">
      {description && (
        <Text fz="sm" fw={600}>
          {description}
        </Text>
      )}
      <Card shadow="lg" p="lg" w="100%" ref={printRef}>
        <QRCode renderAs="svg" value={link} id="qr-gen" style={{ width: '100%', height: '100%' }} />
      </Card>
    </Stack>
  );
};

const ShareQRCodeModalFooter = ({ link, print }: { link: string; print: () => void }) => {
  const Platform = usePlatformService();
  const clipboard = useClipboard({ timeout: 500 });

  const { platformShare } = usePlatformShare();

  const handleShareLink = async () => {
    await platformShare(
      {
        share_options: { url: link },
      },
      () => clipboard.copy(link)
    );
  };

  useEffect(() => {
    if (clipboard.copied) {
      notifications.show({
        variant: 'success',
        message: 'Link copied to clipboard',
      });
    }
  }, [clipboard.copied]);
  return (
    <Modal.AffixedFooter>
      <FullWidthButtonGroup>
        {!Platform.isNative && (
          <ButtonMantine variant="subtle" onClick={() => void print()}>
            Print QR Code
          </ButtonMantine>
        )}
        <ButtonMantine miw="50%" onClick={() => void handleShareLink()}>
          {Platform.isDesktopBrowser ? 'Copy Page Link' : 'Share Page Link'}
        </ButtonMantine>
      </FullWidthButtonGroup>
    </Modal.AffixedFooter>
  );
};

const ShareQRCodeModal = ({ link, description, opened, onClose }: ShareQRCodeModalProps) => {
  const { targetRef: printRef, print } = Printty.usePrint({
    headerProps: { title: description ?? 'Share QR Code' },
  });

  return (
    <Modal title="Share QR Code" opened={opened} onClose={onClose}>
      <ShareQRCodeModalContent link={link} description={description} printRef={printRef} />
      <ShareQRCodeModalFooter link={link} print={() => void print()} />
    </Modal>
  );
};

export { ShareQRCodeModal };
